<template>
    <div ref="tooltipRef" class="trends-tooltip">
        <VJoyPanel
            v-if="tooltipState"
            class="trends-tooltip__panel"
            :class="{
                'trends-tooltip__panel--right': tooltipState.tooltipPosition.isRight,
                'trends-tooltip__panel--bottom': tooltipState.tooltipPosition.isBottom,
            }"
        >
            <template #panel-title-action>
                <VJoyIconButton icon="cross" variant="ghost" size="small" @click="handleClose" />
            </template>
            <template #panel-title-icon>
                <span
                    class="trends-tooltip__category-dot"
                    :style="{backgroundColor: tooltipState.point.backgroundColor, borderColor: tooltipState.point.borderColor}"
                />
            </template>
            <template #panel-title>{{ tooltipState.point.displayName }}</template>
            <template #panel-body>
                <slot name="body">
                    <div class="trends-tooltip__stats">{{ props.axisX.label }}: {{ tooltipState.point.x.toFixed(2) }}</div>
                    <div>
                        {{ tooltipState.point.displayDescription }}
                    </div>
                </slot>
            </template>
        </VJoyPanel>
    </div>
</template>

<script setup lang="ts">
    import {computed, onMounted, onUnmounted, useTemplateRef, watch} from 'vue';
    import {VJoyPanel, VJoyIconButton} from '@maltjoy/core-vue';
    import {useTrendsChartTooltip} from '../../../composables/trends/useTrendsChartTooltip';

    const props = defineProps<{
        axisX: {label: string};
    }>();

    const emit = defineEmits<{
        (e: 'close:after'): void;
    }>();

    const {handleClose, tooltipState} = useTooltipState();
    const {hoverPanelPositionX, hoverPanelPositionY} = useTooltipPosition();
    useTooltipKeyboardEvents();
    useTooltipCleaning();

    function useTooltipState() {
        const {clearTooltip, tooltipState} = useTrendsChartTooltip();

        function handleClose() {
            clearTooltip();
            emit('close:after');
        }

        return {
            // a-z functions
            handleClose,
            // a-z
            tooltipState,
        };
    }

    function useTooltipPosition() {
        const {tooltipState} = useTooltipState();

        const hoverPanelPositionX = computed(() => {
            return tooltipState.value ? `${tooltipState.value.pointPosition.x}px` : 'auto';
        });
        const hoverPanelPositionY = computed(() => {
            return tooltipState.value ? `${tooltipState.value.pointPosition.y}px` : 'auto';
        });
        return {
            hoverPanelPositionX,
            hoverPanelPositionY,
        };
    }

    function useTooltipKeyboardEvents() {
        const {handleClose} = useTooltipState();

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                handleClose();
            }
        }

        onMounted(() => {
            window.addEventListener('keydown', handleKeyDown);
        });

        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeyDown);
        });
    }

    function useTooltipCleaning() {
        const {tooltipState, handleClose} = useTooltipState();
        const tooltipRef = useTemplateRef<HTMLDivElement>('tooltipRef');

        function handleClickOutsideOfTooltip(event: FocusEvent) {
            if (event.target instanceof HTMLElement && !event.target.closest('.trends-tooltip__panel')) {
                handleClose();
            }
        }

        function handleMouseLeave() {
            handleClose();
        }

        watch(tooltipState, (newTooltipState) => {
            if (newTooltipState) {
                window.addEventListener('click', handleClickOutsideOfTooltip);
                tooltipRef.value?.parentElement?.addEventListener('mouseleave', handleMouseLeave);
            } else {
                window.removeEventListener('click', handleClickOutsideOfTooltip);
                tooltipRef.value?.parentElement?.removeEventListener('mouseleave', handleMouseLeave);
            }
        });
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .trends-tooltip {
        &__panel {
            &.joy-panel {
                --panel-width: min(calc(100vw - var(--joy-core-spacing-4)), 600px);
                box-shadow: var(--joy-core-elevation-1);
                width: var(--panel-width);
                max-width: var(--panel-width);

                position: fixed;
                z-index: var(--joy-core-z-index-fixed-content);

                inset-inline: 0;
                margin-inline: var(--joy-core-spacing-2);

                .joy-panel__header-title {
                    align-items: center;
                }
                .trends-chart__category-dot {
                    width: 0.8em;
                    height: 0.8em;
                }

                .joy-panel__header {
                    pointer-events: visible;
                }

                bottom: var(--joy-core-spacing-2);
                &.trends-tooltip__panel--bottom {
                    top: var(--joy-core-spacing-6);
                    bottom: unset;
                }

                &,
                & * {
                    // Fix behavior when hovering the panel
                    pointer-events: visible;
                }

                @include mq.screen_md(min) {
                    --hover-panel-position-x: v-bind(hoverPanelPositionX);
                    --hover-panel-position-y: v-bind(hoverPanelPositionY);
                    --hover-panel-height: 400px;

                    inset-inline: auto;
                    margin-inline: var(--joy-core-spacing-4);

                    // X positionning
                    &:not(.trends-tooltip__panel--right) {
                        --hover-panel-x-left: calc(var(--hover-panel-position-x));

                        left: var(--hover-panel-x-left);
                        // How to avoid going out of the screen?
                        right: unset;
                    }
                    &.trends-tooltip__panel--right {
                        // How to avoid going out of the screen?
                        left: unset;
                        right: calc(100vw - var(--hover-panel-position-x));
                    }

                    // Y positionning
                    &:not(.trends-tooltip__panel--bottom) {
                        --hover-panel-y-bottom: calc(var(--hover-panel-position-y));
                        top: var(--hover-panel-y-bottom);
                        // How to avoid going out of the screen?
                        bottom: unset;
                    }
                    &.trends-tooltip__panel--bottom {
                        // How to avoid going out of the screen?
                        --hover-panel-y: calc(100vh - var(--hover-panel-position-y));
                        --hover-panel-y-max: calc(100vh - var(--hover-panel-height) - var(--joy-core-spacing-4));
                        top: unset;
                        bottom: min(var(--hover-panel-y), var(--hover-panel-y-max));
                    }
                }
            }
        }

        &__category-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            aspect-ratio: 1 / 1;
        }

        &__stats {
            font-weight: var(--joy-font-weight-bold);
        }
    }
</style>
